/**
 * client/audio/events.ts — predictable / game events -> BO2 alias names (pure).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * `plr` aliases are the first-person (2D) versions, `npc` the third-person (3D) ones, following
 * the game's convention (_footsteps.csc buildandcachesoundalias: "fly_" + movementtype + "_" +
 * (firstperson ? "plr" : "npc") + "_" + surfacetype) [research/audio.md §4].
 * Weapon sounds come from the weapon files' *Sound / *SoundPlayer fields
 * (shared/weapons/weapons.generated.ts raw fields).
 */
import { SURFACE_TYPES } from '@shared/collision/types';
import { getWeaponDef } from '@shared/weapons/defs';
import { PEv, WeaponId } from '@shared/types';

export function surfaceName(surfaceType: number): string {
  return SURFACE_TYPES[surfaceType] ?? 'default';
}

/** A weapon-file sound field (e.g. 'fireSound'), plr variant when firstPerson ('fireSoundPlayer'). */
export function weaponSound(weapon: number, field: string, firstPerson: boolean): string {
  const raw = getWeaponDef(weapon as WeaponId).raw as Record<string, unknown>;
  const k = firstPerson ? `${field}Player` : field;
  const v = raw[k];
  if (typeof v === 'string' && v) return v;
  // some fields only exist in one form (e.g. meleeHitSound / bounceSound / projExplosionSound)
  const v2 = raw[field];
  return typeof v2 === 'string' ? v2 : '';
}

const STEP_TYPE: Partial<Record<number, string>> = {
  [PEv.FOOTSTEP_WALK]: 'step_walk',
  [PEv.FOOTSTEP_RUN]: 'step_run',
  [PEv.FOOTSTEP_SPRINT]: 'step_sprint',
  // crouched footsteps: the engine picks crouch_walk / crouch_run by speed; we only get the event
  // kind, so crouch steps use crouch_walk (ESTIMATE)
  [PEv.FOOTSTEP_CROUCH]: 'step_crouch_walk',
  [PEv.FOOTSTEP_PRONE]: 'step_prone',
};

/**
 * Alias candidates for a predictable event, most specific first (the engine falls back to the
 * "default" surface when a surface has no alias). Returns [] for events with no sound.
 */
export function aliasesForPEv(ev: number, parm: number, firstPerson: boolean): string[] {
  const who = firstPerson ? 'plr' : 'npc';
  const step = STEP_TYPE[ev];
  if (step) {
    const s = surfaceName(parm & 0xff);
    return [`fly_${step}_${who}_${s}`, `fly_${step}_${who}_default`];
  }
  switch (ev) {
    case PEv.JUMP: {
      // _footsteps.csc playerjump: the step_run alias of the surface
      const s = surfaceName(parm & 0xff);
      return [`fly_step_run_${who}_${s}`, `fly_step_run_${who}_default`];
    }
    case PEv.LAND: {
      const s = surfaceName(parm & 0xff);
      // parm = surface | severity << 8 | viewDip << 10; severity 3 = damage landing (pmove #P1)
      const severity = (parm >> 8) & 0x3;
      if (severity >= 3) return [`fly_land_damage_${who}`];
      return [`fly_land_${who}_${s}`, `fly_land_${who}_default`];
    }
    case PEv.DIVE_LAND: {
      const s = surfaceName(parm & 0xff);
      return [`fly_dtp_land_${who}_${s}`, `fly_dtp_land_${who}_default`];
    }
    case PEv.MANTLE:
      return firstPerson ? ['fly_gear_mantle_plr'] : [];
    case PEv.WEAPON_FIRE:
    case PEv.WEAPON_FIRE_LAST:
      return nonEmpty(weaponSound(parm & 0xff, 'fireSound', firstPerson));
    case PEv.WEAPON_DRYFIRE:
      return nonEmpty(weaponSound(parm & 0xff, 'emptyFireSound', firstPerson));
    case PEv.WEAPON_RAISE: {
      const w = parm & 0xff, kind = (parm >> 8) & 0xff;
      return nonEmpty(weaponSound(w, kind === 1 ? 'firstRaiseSound' : 'raiseSound', firstPerson));
    }
    case PEv.WEAPON_DROP:
      return nonEmpty(weaponSound(parm & 0xff, 'putawaySound', firstPerson));
    case PEv.ADS_UP:
      return firstPerson ? nonEmpty(weaponSound(parm & 0xff, 'adsRaiseSound', true)) : [];
    case PEv.ADS_DOWN:
      return firstPerson ? nonEmpty(weaponSound(parm & 0xff, 'adsLowerSound', true)) : [];
    case PEv.MELEE_SWING:
    case PEv.MELEE_LUNGE:
      return nonEmpty(weaponSound(parm & 0xff, 'meleeSwipeSound', firstPerson));
    case PEv.OFFHAND_THROW:
      // hatchet_mp fireSound(Player) = wpn_hatchet_throw_npc / _plr
      return nonEmpty(weaponSound(parm & 0xff, 'fireSound', firstPerson));
    default:
      return [];
  }
}

function nonEmpty(a: string): string[] {
  return a ? [a] : [];
}

/** Missile impact on a surface (impactType "bolt" for the crossbow and the ballistic knife). */
export function impactAliases(weapon: number, surfaceType: number): string[] {
  const s = surfaceName(surfaceType);
  if (weapon === WeaponId.HATCHET) {
    // hatchet_mp projExplosionSound fly_hatchet_hit_npc does not exist in any MP zone; flesh hits
    // fall back to the flesh bounce (ESTIMATE, research/audio.md §4)
    if (s === 'flesh' || s === 'player') return ['wpn_hatchet_bounce_flesh'];
    return [`wpn_hatchet_bounce_${s}`, 'wpn_hatchet_bounce_default'];
  }
  if (s === 'player') return ['prj_bolt_impact_player', 'prj_bolt_impact_flesh'];
  return [`prj_bolt_impact_${s}`, 'prj_bolt_impact_default'];
}

/** Hatchet bounce: bounceSound + "_" + surface. */
export function bounceAliases(weapon: number, surfaceType: number): string[] {
  const base = weaponSound(weapon, 'bounceSound', false) || 'wpn_hatchet_bounce';
  const s = surfaceName(surfaceType);
  return [`${base}_${s}`, `${base}_default`];
}

/**
 * Battle-chatter voice prefix (_battlechatter_mp.gsc: teamprefix[team] + bcVoiceNumber).
 * Hijacked factions: SEALs (vox_st0..3) and PMC (vox_pm0..3). FFA players get a random body
 * (PlayerInfo.body 0 = SEALs, 1 = PMC); the voice number is ESTIMATE clientNum % 4.
 */
export function voicePrefix(body: number, clientNum: number): string {
  return `vox_${body === 1 ? 'pm' : 'st'}${((clientNum % 4) + 4) % 4}`;
}

/**
 * Announcer lines arrive as "vox_wm_<dialog>" (_globallogic_audio.gsc uses the vox_wm_ prefix in
 * wager matches). No vox_wm_* alias exists in any MP zone, so in the shipped game they are silent;
 * the remake substitutes the player's faction announcer (vox_st6_ / vox_pmc_) when that line
 * exists (ESTIMATE / design choice, research/audio.md §4 "Announcer").
 */
export function announcerCandidates(alias: string, body: number): string[] {
  const out = [alias];
  if (alias.startsWith('vox_wm_')) {
    const rest = alias.slice('vox_wm_'.length);
    out.push(`vox_${body === 1 ? 'pmc' : 'st6'}_${rest}`);
  } else if (!alias.startsWith('vox_') && !alias.startsWith('mpl_') && !alias.startsWith('mus_')) {
    // bare dialog key ("sns_start", "sns_hum")
    out.push(`vox_${body === 1 ? 'pmc' : 'st6'}_${alias}`);
  }
  return out;
}
